import React, { useState } from 'react';
import { X, ArrowRight, ArrowLeft } from 'lucide-react';
import WelcomeScreen from './WelcomeScreen';
import LocationQuestion from './LocationQuestion';
import QuizQuestion from './QuizQuestion';
import EmailCapture from './EmailCapture';
import LoadingScreen from './LoadingScreen';
import ResultScreen from './ResultScreen';
import MarbleheadPricing from './MarbleheadPricing';
import { QuizAnswer, RoastType } from './types';
import { calculateRoastType } from './utils';
import { supabase } from '../../lib/supabase';
import OrderForm from '../Order/OrderForm';
import OrderSuccess from '../Order/OrderSuccess';

interface QuizModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function QuizModal({ isOpen, onClose }: QuizModalProps) {
  const [currentStep, setCurrentStep] = useState<'welcome' | 'location' | 'quiz' | 'email' | 'loading' | 'result' | 'marblehead-pricing' | 'order' | 'success'>('welcome');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswer[]>([]);
  const [email, setEmail] = useState('');
  const [roastType, setRoastType] = useState<RoastType | null>(null);
  const [skippedQuiz, setSkippedQuiz] = useState(false);
  const [isMarblehead, setIsMarblehead] = useState(false);
  const [selectedTier, setSelectedTier] = useState<any>(null);
  const [customerName, setCustomerName] = useState('');

  if (!isOpen) return null;

  const handleStartQuiz = () => {
    setCurrentStep('location');
  };

  const handleLocationSelect = (marbleheadResident: boolean) => {
    setIsMarblehead(marbleheadResident);
    if (skippedQuiz) {
      // If they skipped the quiz, go straight to result with default email
      const calculatedRoastType = 'medium';
      setRoastType(calculatedRoastType);
      setEmail(''); // No email required for quiz skippers
      
      if (marbleheadResident) {
        setCurrentStep('marblehead-pricing');
      } else {
        setCurrentStep('result');
      }
    } else {
      // If they're taking the quiz, proceed to quiz questions
      setCurrentStep('quiz');
    }
  };

  const handleSkipQuiz = () => {
    setCurrentStep('location');
  };

  const handleSkipToEmail = () => {
    setRoastType('medium');
    setSkippedQuiz(true);
    setCurrentStep('location');
  };

  const handleAnswerQuestion = (answer: QuizAnswer) => {
    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);

    if (currentQuestion < 4) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Quiz complete, move to email capture
      setCurrentStep('email');
    }
  };

  const handleEmailSubmit = (emailValue: string) => {
    setEmail(emailValue);
    const calculatedRoastType = skippedQuiz ? 'medium' : calculateRoastType(answers);
    setRoastType(calculatedRoastType);
    
    // Update the subscription record with quiz completion and roast type
    if (emailValue) {
      updateSubscriptionRecord(emailValue, calculatedRoastType, !skippedQuiz);
    }
    
    if (skippedQuiz) {
      // Quiz skippers skip the loading screen entirely
      if (isMarblehead) {
        setCurrentStep('marblehead-pricing');
      } else {
        setCurrentStep('result');
      }
    } else {
      // Quiz takers see the loading screen
      setCurrentStep('loading');
      
      // Show loading for exactly 12 seconds (3 messages × 4 seconds each)
      setTimeout(() => {
        if (isMarblehead) {
          setCurrentStep('marblehead-pricing');
        } else {
          setCurrentStep('result');
        }
      }, 12000);
    }
  };

  const updateSubscriptionRecord = async (email: string, roastType: RoastType, quizCompleted: boolean) => {
    try {
      const { error } = await supabase
        .from('subscriptions')
        .upsert({
          email,
          roast_type: roastType,
          subscription_months: 3,
          status: quizCompleted ? 'quiz_completed' : 'email_captured',
          quiz_completed: quizCompleted,
          quiz_answers: quizCompleted ? answers : null,
          completed_at: quizCompleted ? new Date().toISOString() : null
        }, {
          onConflict: 'email'
        });

      if (error) {
        console.error('Error updating subscription record:', error);
      }
    } catch (err) {
      console.error('Error updating subscription record:', err);
    }
  };

  const handleProceedToOrder = (tier: any) => {
    console.log('handleProceedToOrder called with tier:', tier);
    setSelectedTier(tier);
    // Update roast type if user changed it in Marblehead pricing
    if (tier.selectedRoastType) {
      setRoastType(tier.selectedRoastType);
    }
    // Go directly to order form for Marblehead customers
    setCurrentStep('order');
  };

  const handleOrderSuccess = (name: string, isMarblehead: boolean, selectedTier: any) => {
    setCustomerName(name);
    setCurrentStep('success');
  };

  const handleBackToSelection = () => {
    setCurrentStep('marblehead-pricing');
  };
  const handlePrevious = () => {
    if (currentStep === 'quiz' && currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setAnswers(answers.slice(0, -1));
    } else if (currentStep === 'location') {
      setCurrentStep('welcome');
    } else if (currentStep === 'email') {
      setCurrentStep('quiz');
      setCurrentQuestion(4);
    } else if (currentStep === 'marblehead-pricing') {
      setCurrentStep('loading');
    }
  };

  const handleReset = () => {
    setCurrentStep('welcome');
    setCurrentQuestion(0);
    setAnswers([]);
    setEmail('');
    setRoastType(null);
    setSkippedQuiz(false);
    setIsMarblehead(false);
    setSelectedTier(null);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="relative bg-[#132947] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-lg transition-colors z-10"
        >
          <X className="w-6 h-6 text-white" />
        </button>

        <div className="p-8">
          {currentStep === 'welcome' && (
            <WelcomeScreen onStart={handleStartQuiz} onSkip={handleSkipToEmail} />
          )}

          {currentStep === 'location' && (
            <div>
              <button
                onClick={handlePrevious}
                className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <LocationQuestion onLocationSelect={handleLocationSelect} />
            </div>
          )}

          {currentStep === 'quiz' && (
            <div>
              <div className="flex items-center justify-between mb-8">
                <button
                  onClick={handlePrevious}
                  className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
                  disabled={currentQuestion === 0}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>
                <div className="text-white/60">
                  {currentQuestion + 1} of 5
                </div>
              </div>
              <QuizQuestion
                questionNumber={currentQuestion}
                onAnswer={handleAnswerQuestion}
              />
            </div>
          )}

          {currentStep === 'email' && (
            <div>
              <button
                onClick={handlePrevious}
                className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <EmailCapture onSubmit={handleEmailSubmit} />
            </div>
          )}

          {currentStep === 'loading' && (
            <LoadingScreen onComplete={() => setCurrentStep('result')} />
          )}

          {currentStep === 'marblehead-pricing' && roastType && (
            <div>
              <button
                onClick={handlePrevious}
                className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <MarbleheadPricing
                roastType={roastType}
                email={email}
                onProceedToOrder={handleProceedToOrder}
                onBack={handlePrevious}
              />
            </div>
          )}

          {currentStep === 'result' && roastType && (
            <ResultScreen
              roastType={roastType}
              email={email}
              skippedQuiz={skippedQuiz}
              isMarblehead={isMarblehead}
              selectedTier={selectedTier}
              onReset={handleReset}
              onClose={onClose}
            />
          )}

          {currentStep === 'order' && roastType && selectedTier && (
            <div>
              <button
                onClick={handleBackToSelection}
                className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Selection
              </button>
              <OrderForm
                roastType={selectedTier.selectedRoastType || roastType}
                purchaseType={selectedTier.purchaseType || 'monthly'}
                selectedOption={selectedTier}
                email={email || 'guest@bondcoffee.com'}
                selectedBags={selectedTier.selectedBags || 1}
                quizSkipped={skippedQuiz}
                isMarblehead={isMarblehead}
                selectedTier={selectedTier}
                onSuccess={handleOrderSuccess}
                onBack={handleBackToSelection}
              />
            </div>
          )}

          {currentStep === 'success' && (
            <OrderSuccess
              purchaseType={selectedTier?.purchaseType || 'monthly'}
              roastType={selectedTier?.selectedRoastType || roastType || 'medium'}
              customerName={customerName}
              isMarblehead={isMarblehead}
              selectedTier={selectedTier}
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
}